// ============================================================================
// GitHub Repository 연결 모달 (CHG-046: REQ-V46-011)
// ============================================================================

import { useState } from 'react';
import { X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useConnectRepo } from '../../hooks/useGitHub';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

const BRANCH_STRATEGIES = [
  { value: 'GITHUB_FLOW', label: 'GitHub Flow', desc: '단일 브랜치 + PR' },
  { value: 'GIT_FLOW', label: 'Git Flow', desc: 'develop/release/hotfix 브랜치' },
  { value: 'TRUNK_BASED', label: 'Trunk-Based', desc: '짧은 수명 브랜치 + 빈번한 머지' },
  { value: 'CUSTOM', label: 'Custom', desc: '자유 전략' },
] as const;

export default function GitHubRepoConnectModal({ isOpen, onClose, projectId }: Props) {
  const [owner, setOwner] = useState('');
  const [name, setName] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [branchStrategy, setBranchStrategy] = useState('GITHUB_FLOW');
  const [defaultBranch, setDefaultBranch] = useState('main');
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');

  const connectMutation = useConnectRepo(projectId);

  if (!isOpen) return null;

  const handleTest = async () => {
    if (!owner || !name || !accessToken) {
      toast.error('Owner, Repository 이름, Access Token을 모두 입력해주세요.');
      return;
    }
    setTestStatus('testing');
    try {
      const res = await fetch(`https://api.github.com/repos/${owner}/${name}`, {
        headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/vnd.github.v3+json' },
      });
      if (res.ok) {
        const data = await res.json();
        setDefaultBranch(data.default_branch ?? 'main');
        setTestStatus('success');
        setTestMessage(`${data.full_name} — ${data.private ? 'Private' : 'Public'}`);
      } else {
        setTestStatus('error');
        setTestMessage(res.status === 401 ? '토큰이 유효하지 않습니다.' : res.status === 404 ? 'Repository를 찾을 수 없습니다.' : `오류: ${res.status}`);
      }
    } catch {
      setTestStatus('error');
      setTestMessage('연결 테스트에 실패했습니다.');
    }
  };

  const handleConnect = async () => {
    try {
      await connectMutation.mutateAsync({ owner, name, accessToken, branchStrategy, defaultBranch });
      toast.success('Repository가 연결되었습니다.');
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Repository 연결에 실패했습니다.';
      toast.error(message);
    }
  };

  const canConnect = testStatus === 'success' && !connectMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">GitHub Repository 연결</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="size-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* PAT */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Personal Access Token</label>
            <input
              type="password"
              value={accessToken}
              onChange={(e) => { setAccessToken(e.target.value); setTestStatus('idle'); }}
              placeholder="ghp_xxxxxxxxxxxx"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-sm text-gray-400">repo 권한이 필요합니다.</p>
          </div>

          {/* Owner / Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
              <input
                value={owner}
                onChange={(e) => { setOwner(e.target.value); setTestStatus('idle'); }}
                placeholder="organization or user"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Repository</label>
              <input
                value={name}
                onChange={(e) => { setName(e.target.value); setTestStatus('idle'); }}
                placeholder="repo-name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Test button + result */}
          <div>
            <button
              onClick={handleTest}
              disabled={testStatus === 'testing' || !owner || !name || !accessToken}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
            >
              {testStatus === 'testing' && <Loader2 className="size-4 animate-spin" />}
              연결 테스트
            </button>
            {testStatus === 'success' && (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="size-4" /> {testMessage}
              </p>
            )}
            {testStatus === 'error' && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="size-4" /> {testMessage}
              </p>
            )}
          </div>

          {/* Branch Strategy */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">브랜칭 전략</label>
            <div className="grid grid-cols-2 gap-2">
              {BRANCH_STRATEGIES.map((s) => (
                <label
                  key={s.value}
                  className={`flex flex-col p-3 border rounded-lg cursor-pointer transition-colors ${
                    branchStrategy === s.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="branchStrategy"
                      value={s.value}
                      checked={branchStrategy === s.value}
                      onChange={(e) => setBranchStrategy(e.target.value)}
                      className="text-primary-600"
                    />
                    <span className="text-sm font-medium text-gray-900">{s.label}</span>
                  </div>
                  <span className="text-sm text-gray-500 mt-1 ml-6">{s.desc}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Default Branch */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">기본 브랜치</label>
            <input
              value={defaultBranch}
              onChange={(e) => setDefaultBranch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleConnect}
            disabled={!canConnect}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {connectMutation.isPending && <Loader2 className="size-4 animate-spin" />}
            연결
          </button>
        </div>
      </div>
    </div>
  );
}
